import { useEffect, useRef } from "react";
import { ApolloClient, gql, useApolloClient, useQuery } from "@apollo/client";
import { useRecoilState, useRecoilValue } from "recoil";

import { useUser } from "MVVM/Hooks/useUser";
import { GET_ELIGIBILITY_COMPETITIONS } from "MVVM/GraphQL/competitions";
import { CompetitionEligibilityState } from "MVVM/State/competitions";
import {
  IClipCompetitionStatus,
  IEligibilityCompetition,
} from "MVVM/Views/Competitions/@types";

const COMPETITION_POOL_SIZE = 25;
const ELIGIBLE_CLIP_CAP = 200;

type TBatchEligibleResult = Record<
  string,
  { nodes: { shareId: string | null }[] } | null
>;

// One aliased eligibleClips field per competition, so every competition's
// eligible-clip set comes back in a single request instead of N. Each clip set
// is capped (no pagination) since this only powers an indicator.
const buildBatchQuery = (count: number) => {
  const varDefs = Array.from(
    { length: count },
    (_, i) => `$c${i}: String!`,
  ).join(", ");
  const fields = Array.from(
    { length: count },
    (_, i) =>
      `c${i}: eligibleClips(competitionId: $c${i}, first: ${ELIGIBLE_CLIP_CAP}) { nodes { shareId } }`,
  ).join("\n");
  return gql`query BatchEligibleClipIds(${varDefs}) { ${fields} }`;
};

const fetchEligibilityByShareId = async (
  client: ApolloClient<object>,
  competitions: IEligibilityCompetition[],
): Promise<Record<string, IClipCompetitionStatus[]>> => {
  const variables = Object.fromEntries(
    competitions.map((competition, i) => [`c${i}`, competition.id]),
  );

  const { data } = await client.query<TBatchEligibleResult>({
    query: buildBatchQuery(competitions.length),
    variables,
    fetchPolicy: "network-only",
  });

  const byShareId: Record<string, IClipCompetitionStatus[]> = {};
  competitions.forEach((competition, i) => {
    const submitted = new Set(
      (competition.myEntry?.submittedClips ?? [])
        .map((clip) => clip.shareId)
        .filter((shareId): shareId is string => !!shareId),
    );

    // eligibleClips includes already-submitted clips, so the eligible set plus
    // the submitted set is the full list of competitions to surface per clip.
    const shareIds = new Set<string>(submitted);
    for (const node of data?.[`c${i}`]?.nodes ?? []) {
      if (node?.shareId) shareIds.add(node.shareId);
    }

    for (const shareId of shareIds) {
      (byShareId[shareId] ||= []).push({
        competition,
        submitted: submitted.has(shareId),
      });
    }
  });
  return byShareId;
};

// Loads the active competitions and, in one batched call, the logged-in user's
// clips that already pass each one's criteria (the backend does the matching).
// Flattened into a shareId -> competitions map in Recoil so any clip surface
// can flag eligibility with a synchronous lookup. Mount once per view.
export const usePopulateCompetitionEligibility = () => {
  const client = useApolloClient();
  const { allstarUser } = useUser();
  const [{ loaded }, setEligibility] = useRecoilState(
    CompetitionEligibilityState,
  );
  const startedRef = useRef(false);

  const { data: compsData } = useQuery(GET_ELIGIBILITY_COMPETITIONS, {
    variables: { sort: "ENDING_SOONEST", first: COMPETITION_POOL_SIZE },
    fetchPolicy: "cache-first",
    skip: !allstarUser.loggedIn,
  });

  useEffect(() => {
    if (!allstarUser.loggedIn || startedRef.current || loaded) return;

    const now = Date.now();
    const active = (
      (compsData?.creatorCompetitions?.nodes ?? []) as IEligibilityCompetition[]
    ).filter(
      (competition) =>
        competition.visibility === "public" &&
        new Date(competition.startsAt).getTime() <= now &&
        new Date(competition.endsAt).getTime() > now,
    );

    if (active.length === 0) return;
    startedRef.current = true;

    (async () => {
      const byShareId = await fetchEligibilityByShareId(client, active);
      setEligibility({ byShareId, loaded: true });
    })();
  }, [allstarUser.loggedIn, compsData, client, loaded, setEligibility]);
};

export const useClipCompetitions = (
  shareId?: string | null,
): IClipCompetitionStatus[] => {
  const { byShareId } = useRecoilValue(CompetitionEligibilityState);
  if (!shareId) return [];
  return byShareId[shareId] ?? [];
};
